// Portal › Özet — /portal/kpi/ozet (M16 T19, DR3): erişim-filtreli, onay odaklı. Şerit "size açık N ·
// K girildi · M danışman onayında · J onaylandı" (countClientFillStatus — payda yalnız `open`
// hücreler), sekmeler Tümü / Danışman onayında / Boş, stat kartları (KpiStatCards — grid zaten erişim
// filtreli), sağ panel "Onay durumu" (danışman adı/e-postası set DTO'sunda yok → "Danışmanınız",
// son onay tarihi, bekleyen N). Kontrol boyutu ve AI Kontrolü YOK (DR4). Boş: "İlk değerinizi girin".
// Portal turu (2026-10-06): ≤1024 tek kolon (panel alta), stat kartları ≤640 tek sütun (scoped
// <style> — KpiStatCards danışmanla ortak, dokunulmaz), tablo kendi konteynerinde kayar (P1-5);
// panelde jenerik "Danışman — Danışmanınız" satırı kaldırıldı (P3-21); TODO-KPI-16 ile set DTO'su
// `consultant` taşıdığından satır yalnız gerçek ad/e-posta varken geri geldi (mailto); "Boş"
// sekmesi yalnız size açık göstergeleri sayar; çok işletmede seçili işletme (grid store'da
// daraltılmış, P1-1).

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EmptyState, LoadingState } from "../../components/common/StateSurface";
import {
  KpiComparisonTable,
  rowMatchesTab,
  type KpiComparisonRow,
  type OzetTab,
} from "../../components/kpi/KpiComparisonTable";
import { KpiPageHeader } from "../../components/kpi/KpiPageHeader";
import { KpiStatCards } from "../../components/kpi/KpiStatCards";
import { PortalKpiBar } from "../../components/kpi/PortalKpiBar";
import { KPI_TOTAL_KIND_LABELS } from "@mahir/shared";
import { KPI_COPY } from "../../lib/kpi-ui";
import {
  countClientFillStatus,
  lastVerifiedAt,
  PORTAL_CONSOLIDATED,
  portalTotalKind,
} from "../../lib/portal-kpi-ui";
import { useMediaQuery } from "../../lib/use-media-query";
import { usePortalKpiStore } from "../../stores/portal-kpi-store";

const P = KPI_COPY.portal;
type PortalTab = Extract<OzetTab, "all" | "approval" | "missing">;

const btn: React.CSSProperties = {
  padding: "6px 12px",
  borderRadius: 8,
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  fontSize: "0.82rem",
  color: "var(--color-text)",
  minHeight: 34,
};

export function PortalKpiOzetPage(): JSX.Element {
  const navigate = useNavigate();
  const setsStatus = usePortalKpiStore((s) => s.setsStatus);
  const sets = usePortalKpiStore((s) => s.sets);
  const setId = usePortalKpiStore((s) => s.setId);
  const grid = usePortalKpiStore((s) => s.grid);
  const error = usePortalKpiStore((s) => s.error);
  const years = usePortalKpiStore((s) => s.years);
  const entityId = usePortalKpiStore((s) => s.entityId);
  const entityChoice = usePortalKpiStore((s) => s.entityChoice);
  const stacked = useMediaQuery("(max-width: 1024px)");
  const narrow = useMediaQuery("(max-width: 640px)");
  const scopedEntityId = entityChoice && entityId !== PORTAL_CONSOLIDATED ? entityId : null;
  // Y6 — toplam görünümünde kartların neyi topladığı `totalKind`'den: kapsamlıya "Konsolide" yazılmaz
  const totalKind = portalTotalKind(
    grid,
    sets.find((x) => x.id === setId),
  );
  const totalCaption =
    entityChoice && entityId === PORTAL_CONSOLIDATED && totalKind !== "none"
      ? KPI_TOTAL_KIND_LABELS[totalKind]
      : null;
  const [tab, setTab] = useState<PortalTab>("all");
  const [year, setYear] = useState<number | null>(null);

  const curYear = year ?? years[years.length - 1] ?? new Date().getFullYear();
  const prevYear = years.includes(curYear - 1) ? curYear - 1 : null;
  const set = sets.find((s) => s.id === setId);

  const rows = useMemo<KpiComparisonRow[]>(() => {
    if (!grid) return [];
    const leaf = (id: string): boolean => !grid.indicators.some((i) => i.parentId === id);
    return grid.indicators
      .filter((i) => leaf(i.id))
      .map((indicator) => ({
        indicator,
        cur: indicator.consolidated?.[curYear],
        prev: prevYear ? indicator.consolidated?.[prevYear] : undefined,
      }));
  }, [grid, curYear, prevYear]);
  const fill = useMemo(
    () => (grid ? countClientFillStatus(grid, scopedEntityId ?? undefined) : null),
    [grid, scopedEntityId],
  );
  const lastApproval = useMemo(() => (grid ? lastVerifiedAt(grid) : null), [grid]);
  // "Boş" yalnız doldurabileceğiniz (open) göstergeler — 🔒 satırlar şeritle çelişmesin (P3-21)
  const matches = (r: KpiComparisonRow, t: PortalTab): boolean =>
    rowMatchesTab(r, t) && (t !== "missing" || r.indicator.effectiveClientAccess === "open");
  const counts = {
    all: rows.length,
    approval: rows.filter((r) => matches(r, "approval")).length,
    missing: rows.filter((r) => matches(r, "missing")).length,
  };
  const visible = rows.filter((r) => matches(r, tab));

  const tabBtn = (key: PortalTab, label: string, n: number): JSX.Element => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === key}
      onClick={() => setTab(key)}
      className="px-3 py-2 text-sm"
      style={{
        fontWeight: 600,
        color: tab === key ? "var(--color-primary)" : "var(--color-text-2)",
        borderBottom: `2px solid ${tab === key ? "var(--color-primary)" : "transparent"}`,
        marginBottom: -1,
        minHeight: 40,
      }}
    >
      {label} ({n})
    </button>
  );

  // güvenlik/UX taraması 2026-10-08 (U-H2/U-M7): yüklenemedi dallarında "Tekrar dene"
  const retryButton = (onClick: () => void): JSX.Element => (
    <button type="button" style={btn} onClick={onClick}>
      {P.retry}
    </button>
  );
  const retrySets = (): void => {
    const uid = usePortalKpiStore.getState().userId;
    if (uid) void usePortalKpiStore.getState().loadSets(uid, { force: true });
  };

  let body: React.ReactNode;
  if (setsStatus === "idle" || setsStatus === "loading")
    body = <LoadingState label={KPI_COPY.ozet.loading} />;
  else if (setsStatus === "error")
    body = <EmptyState title={P.loadFailed} action={retryButton(retrySets)} />;
  else if (sets.length === 0) body = <EmptyState title={P.noAccessTitle} hint={P.noAccessBody} />;
  else if (error && !grid)
    body = (
      <EmptyState
        title={P.loadFailed}
        hint={error}
        action={retryButton(() => void usePortalKpiStore.getState().refreshGrid())}
      />
    );
  else if (!grid || !fill) body = <LoadingState label={KPI_COPY.ozet.loading} />;
  else if (fill.filled === 0 && fill.awaiting === 0 && fill.verified === 0) {
    body = (
      <EmptyState
        title={P.ozet.emptyTitle}
        hint={P.ozet.emptyBody}
        action={
          <button
            type="button"
            className="rounded-lg px-4 py-2 text-sm font-semibold"
            style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            onClick={() => void navigate("/portal/kpi/tablo")}
          >
            {P.ozet.goTable}
          </button>
        }
      />
    );
  } else {
    body = (
      <div
        className="grid gap-4 items-start"
        style={{ gridTemplateColumns: stacked ? "minmax(0,1fr)" : "minmax(0,1fr) 300px" }}
        data-portal-ozet=""
      >
        {narrow ? (
          // KpiStatCards danışmanla ortak bileşen (dokunulmaz): 390'da 4 dar sütun üst üste biniyordu
          <style>{`[data-portal-ozet] [data-portal-ozet-cards] > div { grid-template-columns: minmax(0,1fr) !important; }`}</style>
        ) : null}
        <div className="min-w-0">
          <div
            className="flex items-center gap-4 flex-wrap text-sm mb-4 px-3 py-2 rounded-xl"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
            aria-label="Durum şeridi"
            data-testid="portal-ozet-strip"
          >
            {P.ozet.strip(fill)}
          </div>
          {totalCaption ? (
            <div
              data-testid="portal-ozet-total-caption"
              className="text-xs mb-2"
              style={{ color: "var(--color-text-3)" }}
            >
              <span aria-hidden="true">Σ</span> {totalCaption}
            </div>
          ) : null}
          <div data-portal-ozet-cards="">
            <KpiStatCards grid={grid} year={curYear} prevYear={prevYear} />
          </div>
          <div
            role="tablist"
            className="flex gap-1 mb-0"
            style={{ borderBottom: "1px solid var(--color-border)" }}
          >
            {tabBtn("all", P.ozet.tabs.all, counts.all)}
            {tabBtn("approval", P.ozet.tabs.approval, counts.approval)}
            {tabBtn("missing", P.ozet.tabs.empty, counts.missing)}
          </div>
          <div
            className="rounded-b-xl"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              borderTop: 0,
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 560 }}>
                <KpiComparisonTable
                  rows={visible}
                  tree={{
                    indicators: grid.indicators,
                    allRows: rows,
                    filtered: tab !== "all",
                    screen: "portal-ozet",
                  }}
                  year={curYear}
                  prevYear={prevYear}
                  canCheck={false}
                  hideCheck
                  onOpen={(code) =>
                    void navigate(
                      `/portal/kpi/tablo?highlight=${encodeURIComponent(code)}&hy=${curYear.toString()}`,
                    )
                  }
                  onToggleChecked={() => undefined}
                />
              </div>
            </div>
            <div
              className="flex justify-between text-xs px-3 py-2"
              style={{ color: "var(--color-text-3)", borderTop: "1px solid var(--color-border)" }}
            >
              <span>
                <b style={{ color: "var(--color-primary)" }}>{visible.length}</b> / {counts.all}
              </span>
              <span>Satıra tıkla → Veri Girişi'nde o göstergeye git</span>
            </div>
          </div>
        </div>
        <aside
          aria-label={P.ozet.panelTitle}
          className="rounded-xl p-4 text-sm"
          style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
          data-testid="portal-approval-panel"
        >
          <div className="font-semibold mb-2" style={{ color: "var(--color-text)" }}>
            {P.ozet.panelTitle}
          </div>
          <dl className="grid text-xs" style={{ gridTemplateColumns: "110px 1fr", rowGap: 6 }}>
            {set?.consultant ? (
              <>
                <dt style={{ color: "var(--color-text-3)" }}>{P.ozet.consultant}</dt>
                <dd
                  style={{ margin: 0, color: "var(--color-text)", overflowWrap: "anywhere" }}
                  data-testid="portal-consultant"
                >
                  {set.consultant.fullName} ·{" "}
                  <a
                    href={`mailto:${set.consultant.email}`}
                    style={{ color: "var(--color-primary)" }}
                  >
                    {set.consultant.email}
                  </a>
                </dd>
              </>
            ) : null}
            <dt style={{ color: "var(--color-text-3)" }}>{P.ozet.lastApproval}</dt>
            <dd style={{ margin: 0, color: "var(--color-text)" }}>
              {lastApproval
                ? new Date(lastApproval).toLocaleDateString("tr-TR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : "—"}
            </dd>
            <dt style={{ color: "var(--color-text-3)" }}>{KPI_COPY.portal.ozet.awaitingLabel}</dt>
            <dd
              style={{
                margin: 0,
                color: fill.awaiting > 0 ? "var(--color-warning-text)" : "var(--color-success)",
                fontWeight: 600,
              }}
            >
              {fill.awaiting > 0 ? P.ozet.pendingCount(fill.awaiting) : P.ozet.noPending}
            </dd>
          </dl>
          <p className="text-xs mt-3" style={{ color: "var(--color-text-3)", lineHeight: 1.5 }}>
            {P.passiveNote}
          </p>
        </aside>
      </div>
    );
  }

  return (
    <main
      className={narrow ? "p-4" : "p-8"}
      style={{ fontFamily: "var(--font-sans)", maxWidth: 1600, minWidth: 0 }}
    >
      <KpiPageHeader title={KPI_COPY.titles.ozet} {...(set ? { subtitle: set.name } : {})} />
      {setsStatus === "ready" && sets.length > 0 ? (
        <PortalKpiBar
          showYears
          showEntity
          right={
            <label
              className="text-xs flex items-center gap-1"
              style={{ color: "var(--color-text-2)" }}
            >
              Dönem
              <select
                aria-label="Dönem"
                style={btn}
                value={curYear}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {[...years].reverse().map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
          }
        />
      ) : null}
      {body}
    </main>
  );
}
